import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTeamContext } from '@/contexts/TeamContext';
import { useTeams } from '@/hooks/useTeams';
import { getActiveTeamQueryKey } from '@/utils/query-keys';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

const teamSchema = yup.object({
  name: yup.string().min(1, 'Team name is required'),
  audience: yup.string().min(1, 'Audience is required'),
  description: yup.string().min(1, 'Description is required'),
  industry: yup.string().min(1, 'Industry is required'),
  tone_and_style: yup.string().min(1, 'Tone and style is required'),
  brand_voice: yup.string().min(1, 'Brand voice is required'),
  posting_frequency: yup
    .number()
    .min(1, 'Posting frequency must be at least 1')
    .required('Posting frequency is required'),
  website_url: yup.string().min(1, 'Website URL is required'),
});

type TeamFormValues = yup.InferType<typeof teamSchema>;

export const TeamSettingsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeTeam, updateTeam, deleteTeam, data: teams } = useTeams();
  const { setActiveTeam } = useTeamContext();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [updatingTeams, setUpdatingTeams] = useState(false);

  const form = useForm<TeamFormValues>({
    resolver: yupResolver(teamSchema),
    defaultValues: {
      name: activeTeam.data?.name || '',
      audience: activeTeam.data?.audience || '',
      description: activeTeam.data?.description || '',
      industry: activeTeam.data?.industry || '',
      tone_and_style: activeTeam.data?.tone_and_style || '',
      brand_voice: activeTeam.data?.brand_voice || '',
      posting_frequency: activeTeam.data?.posting_frequency || 0,
      website_url: activeTeam.data?.website_url || '',
    },
  });

  const onSubmit = async (data: TeamFormValues) => {
    if (!activeTeam.data?.id) return;

    try {
      setUpdatingTeams(true);
      toast.info(
        'Updating teams, this may take a few seconds while we learn more about your team!',
      );

      await updateTeam.mutateAsync({
        id: activeTeam.data.id,
        ...data,
      });

      await new Promise<void>((resolve) => {
        const observer = new QueryObserver(queryClient, {
          queryKey: getActiveTeamQueryKey(),
        });

        const unsubscribe = observer.subscribe((result) => {
          if (!result.isFetching) {
            unsubscribe();
            resolve();
          }
        });
      });

      setUpdatingTeams(false);
      toast.success('Team updated successfully!');
    } catch (error) {
      setUpdatingTeams(false);
      console.error('Failed to update team:', error);
      toast.error('Failed to update team, please try again.');
    }
  };

  const handleDeleteTeam = async () => {
    if (!activeTeam.data?.id) return;

    try {
      await deleteTeam.mutateAsync(activeTeam.data.id);

      const remainingTeams =
        teams?.filter((team) => team.id !== activeTeam.data?.id) ?? [];

      if (remainingTeams.length > 0) {
        await setActiveTeam(remainingTeams[0]);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to delete team:', error);
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  if (activeTeam.isLoading) {
    return <SkeletonLoader />;
  }

  if (!activeTeam.data) return null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Team Name</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      This is your team's display name.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Description</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Provide a brief description of your team's purpose and
                      goals.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="industry"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Industry</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      The primary industry or market space your team operates
                      in.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="audience"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Target Audience</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Define who your team serves or targets with your content.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tone_and_style"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Tone and Style</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Define the overall tone and style of your brand's
                      communication.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="brand_voice"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Brand Voice</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Describe your brand's personality and how it communicates.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="posting_frequency"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Weekly Posting Frequency</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      How many times you want to post per weekly.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min={1}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website_url"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Website URL</FormLabel>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      Provide the URL of your company website.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <Input
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between">
          <Button type="submit" isLoading={updatingTeams}>
            Save Changes
          </Button>

          <ConfirmDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            title="Delete Team"
            description="Are you sure you want to delete this team? This action cannot be undone."
            confirmText="Delete Team"
            onConfirm={handleDeleteTeam}
            isConfirming={deleteTeam.isPending}
            variant="destructive"
            trigger={<Button variant="destructive">Delete Team</Button>}
          />
        </div>
      </form>
    </Form>
  );
};

const SkeletonLoader = () => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-24 w-full" />
      </div>
      <div className="flex justify-between">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
  );
};
